'use client'

import { useEffect, useRef } from 'react'

/**
 * Segler ziehen in sanft links-gebogenen Bogen von links unten nach rechts oben.
 * Pfad = quadratische Bézier (from → cp → to) — dadurch gebogene Flugbahn,
 * passend zur leicht nach links geneigten Silhouette.
 * Bank-Angle folgt der Tangente, auf ±50° geclampt. Kein Mirror, kein
 * Rückwärtsflug. Sanftes Fade in/out beim Bildrand für nahtlosen Loop.
 * Farbe kommt aus --glider-color (Theme-abhängig).
 */
type DriftConfig = {
  /** Sekunden pro Durchgang */
  duration: number
  /** Phasenversatz 0..1 — damit die Flieger versetzt starten */
  phase: number
  /** Start- und Endposition relativ zum Wrapper (0..1) */
  fromX: number; fromY: number
  toX: number;   toY: number
  /** Bézier-Controlpoint — bestimmt die Krümmung.
   *  Liegt der Punkt ÜBER der Geraden, biegt der Pfad nach oben (konvex). */
  cpX: number; cpY: number
  /** Basis-Scale (wird ignoriert wenn scaleFrom/scaleTo gesetzt sind) */
  scale: number
  /** Optional: echte Perspektive — Scale interpoliert linear von scaleFrom
   *  (t=0, weit weg) zu scaleTo (t=1, nah am Betrachter). Wenn nur einer der
   *  beiden gesetzt ist, wird scale als Fallback fuer den fehlenden verwendet.
   *  Erzeugt den Eindruck, dass der Flieger aus der Tiefe kommt und am
   *  Betrachter vorbeizieht. */
  scaleFrom?: number
  scaleTo?: number
  /** Optional: Peak-Scale entlang der Bahn. Wenn gesetzt, wird zweistufig
   *  linear interpoliert (scaleFrom → scaleMid → scaleTo). Peak liegt bei
   *  scalePeakT (default 0.5) — damit laesst sich der "Vorbeiflug-Moment"
   *  asymmetrisch auf der Bahn platzieren, z.B. auf der rechten Seite. */
  scaleMid?: number
  scalePeakT?: number
  /** Optional: nicht-lineare Zeit-Verteilung ueber die Bahn. Der lineare
   *  Fortschritt tLinear wird auf t = tLinear^timeEase gemappt. Mit
   *  timeEase > 1 bewegt sich der Flieger langsam am Anfang (weit weg in der
   *  Tiefe) und schnell am Ende (nah am Betrachter, zischt vorbei) — das ist
   *  die echte perspektivische Winkelgeschwindigkeit. Default 1 (linear). */
  timeEase?: number
  /** Grundrotation (zusätzlich zur Tangente). Wird auf ±50° geclampt. */
  extraRotateDeg: number
  /** CSS-Breite */
  width: string
  /** Maximale Opacity (Mitte des Pfads) */
  opacity: number
  /** Optional: Delfinstil — Sinus-Welle auf die Y-Achse.
   *  dolphinAmp = Pixel-Amplitude (vertikaler Hub), dolphinCycles = Anzahl
   *  vollständiger Auf-Ab-Zyklen über den gesamten Durchgang. Bank-Winkel
   *  folgt der resultierenden Gesamt-Tangente, wirkt damit automatisch weich.
   *  dolphinSkew (0..0.9) verzerrt die Phase: der Flieger bleibt länger im
   *  Tief (Gleitflug) und steigt schneller wieder hoch (Thermik-Aufschwung).
   *  0 = symmetrische Sinuswelle, 0.5 = klar asymmetrisch. */
  dolphinAmp?: number
  dolphinCycles?: number
  dolphinSkew?: number
  /** Optional: Zisch-Sound bei Vorbeiflug. Wird bei Überschreiten von
   *  zischTriggerT (default 0.5) ausgelöst. Lautstärke 0..1 — default 0.08. */
  zisch?: boolean
  zischTriggerT?: number
  zischVolume?: number
  /** Optional: Kontinuierlicher Fluggeraeusch-Layer (langanhaltendes Wind-
   *  rauschen/Sirren). Wird als persistente Noise-Source gespielt, deren
   *  Lautstaerke, Mittenfrequenz und Stereo-Pan pro Frame an die visuelle
   *  Position gekoppelt werden: naehere Segler sind lauter + heller + panned
   *  auf die Bildschirmseite. Default: an (weil die visuelle Simulation
   *  mehrere Segler gleichzeitig zeigt). */
  ambientSound?: boolean
  /** Basis-Gain (0..1) bei scale=1.0. Wird mit scaleNow * alpha skaliert.
   *  Default 0.05 — damit 4-5 Segler zusammen ca. 0.2 erreichen. */
  ambientBaseGain?: number
  /** Mittenfrequenz (Hz) bei scale=0.0 (weit weg). Default 700.
   *  Haeufige Werte 500-1200 je nach Segler-"Charakter". */
  ambientFreqFloor?: number
  /** Zuschlag Mittenfrequenz (Hz) bei scale=1.0 (nah am Betrachter). Default 1600.
   *  Naehere Segler klingen heller (mehr Wind-High-Anteil). */
  ambientFreqRange?: number
  /** Optional: Tonales Tragflaechen-Pfeifen (Sinus-Oscillator) zusaetzlich zum
   *  Ambient-Rauschen. Nur sinnvoll fuer schnelle Anflug-Segler. Frequenz
   *  moduliert von whistleFreqLow (fern) → whistleFreqHigh (nah), plus
   *  Doppler-Shift. Gain ist quadratisch an closeness gekoppelt (scharfer
   *  Peak in der Bildmitte, sonst unhoerbar). */
  whistleSound?: boolean
  whistleFreqLow?: number
  whistleFreqHigh?: number
  whistleBaseGain?: number
}

const GLIDER_ASPECT = '462 / 235'
const MAX_BANK = 50

const DEFAULT_SWARM: DriftConfig[] = [
  // 1 — Lead, mittleres Bilddrittel, gross, langsamer Bogen. Phase 0.
  //     +20 deg = ausgeprägte Rechtslage, wirkt wie Kurvenflug.
  {
    duration: 40, phase: 0,
    fromX: -0.30, fromY: 0.60,
    toX:   1.35,  toY:   0.38,
    cpX:   0.52,  cpY:   0.36,
    scale: 1.05, extraRotateDeg: 20,
    width: 'min(34vw, 320px)', opacity: 0.92,
    ambientSound: true, ambientBaseGain: 0.08, ambientFreqFloor: 650, ambientFreqRange: 1800,
  },
  // 2 — Wingman, deutlich hoeher, Phase deutlich versetzt (0.35).
  //     +15 deg Rechtslage (mittelstark, weniger als Lead).
  {
    duration: 40, phase: 0.35,
    fromX: -0.32, fromY: 0.36,
    toX:   1.33,  toY:   0.18,
    cpX:   0.48,  cpY:   0.14,
    scale: 0.82, extraRotateDeg: 15,
    width: 'min(26vw, 240px)', opacity: 0.80,
    ambientSound: true, ambientBaseGain: 0.06, ambientFreqFloor: 850, ambientFreqRange: 2000,
  },
  // 3 — Kleiner Begleiter, jetzt auf Schriftzug-Ebene (gleiche Hoehe wie Delfin),
  //     fliegt also durch den "FREE GLIDE LEAGUE"-Bereich. Phase 0.68 +
  //     Duration 30 (vs Delfin 50, Phase 0.18) vermeidet Kollision.
  //     +12 deg = leichte Schraeglage, passend zur kleineren Silhouette.
  {
    duration: 30, phase: 0.68,
    fromX: -0.28, fromY: 0.12,
    toX:   1.28,  toY:   0.10,
    cpX:   0.50,  cpY:   0.09,
    scale: 0.34, extraRotateDeg: 12,
    width: 'min(14vw, 130px)', opacity: 0.45,
    ambientSound: true, ambientBaseGain: 0.035, ambientFreqFloor: 1100, ambientFreqRange: 2200,
  },
  // 4 — Delfinstil-Flieger: asymmetrische Wellen (laenger unten im Gleitflug,
  //     schneller hoch in den Aufschwung). dolphinSkew 0.5 verzerrt die Sinus-
  //     Phase so, dass der Flieger pro Zyklus etwa 2/3 der Zeit im Tief bleibt.
  //     Bank-Winkel folgt dank dAsymPhase der weichen Wellen-Asymmetrie mit.
  {
    duration: 50, phase: 0.18,
    fromX: -0.28, fromY: 0.13,
    toX:   1.30,  toY:   0.11,
    cpX:   0.50,  cpY:   0.09,
    scale: 0.70, extraRotateDeg: 0,
    width: 'min(22vw, 200px)', opacity: 0.72,
    dolphinAmp: 60,
    dolphinCycles: 3.5,
    dolphinSkew: 0.5,
    ambientSound: true, ambientBaseGain: 0.055, ambientFreqFloor: 550, ambientFreqRange: 1600,
  },
  // 5 — Anflug aus der Ferne mit echter Perspektiv-Geschwindigkeit: startet
  //     winzig ganz oben links und scheint lange zu kriechen (weit weg,
  //     kleine Winkelgeschwindigkeit). timeEase 2.8 sorgt dafuer, dass der
  //     lineare Zeit-Fortschritt auf t^2.8 gemappt wird — am Anfang bewegt er
  //     sich kaum, am Ende zischt er am Countdown/Sekunden-Pille vorbei und
  //     verschwindet rechts im Bruchteil der Zeit. Scale-Peak (1.05) ebenfalls
  //     bei t=0.72 — also weit in der zweiten Haelfte der Bahn.
  //     Duration 40s damit der langsame Anflug auch wirklich Zeit bekommt.
  {
    duration: 45, phase: 0.50,
    fromX: -0.22, fromY: 0.05,
    toX:   1.32,  toY:   0.24,
    cpX:   0.72,  cpY:   0.22,
    scale: 0.8, extraRotateDeg: 10,
    scaleFrom: 0.08, scaleMid: 1.10, scaleTo: 0.55, scalePeakT: 0.75,
    timeEase: 4.2,
    width: 'min(22vw, 210px)', opacity: 0.92,
    zisch: true,
    zischTriggerT: 0.62,
    zischVolume: 0.55,
    ambientSound: true, ambientBaseGain: 0.07, ambientFreqFloor: 750, ambientFreqRange: 1900,
    whistleSound: true, whistleFreqLow: 2200, whistleFreqHigh: 3800, whistleBaseGain: 0.22,
  },
  // 6 — Schnellflug-Diagonale: schneller, flacher Vorbeizug von links-oben
  //     nach rechts-mitte. Realistischer Gleitwinkel (~14deg ueber den
  //     gesamten Pfad), nur leichte Nase-runter-Haltung (+4deg Tangente).
  //     Sanfte Bezier-Senke in der Mitte fuer „dippen-und-rausziehen"-Optik.
  {
    duration: 16, phase: 0.55,
    fromX: -0.18, fromY: 0.18,
    toX:   1.24,  toY:   0.58,
    cpX:   0.55,  cpY:   0.46,
    scale: 0.72, extraRotateDeg: 4,
    scaleFrom: 0.58, scaleTo: 0.82, scalePeakT: 0.55,
    width: 'min(22vw, 200px)', opacity: 0.88,
    zisch: true,
    zischTriggerT: 0.50,
    zischVolume: 0.45,
    ambientSound: true, ambientBaseGain: 0.05, ambientFreqFloor: 900, ambientFreqRange: 2100,
    whistleSound: true, whistleFreqLow: 2400, whistleFreqHigh: 4000, whistleBaseGain: 0.18,
  },
]


function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v
}
function lerp(a: number, b: number, t: number) { return a + (b - a) * t }

function smoothstep(edge0: number, edge1: number, x: number) {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return t * t * (3 - 2 * t)
}

/** Quadratische Bézier: position & tangente (normalisierte Ableitung). */
function bezier2(fromV: number, cp: number, to: number, t: number) {
  const u = 1 - t
  const pos = u * u * fromV + 2 * u * t * cp + t * t * to
  const deriv = 2 * u * (cp - fromV) + 2 * t * (to - cp)
  return { pos, deriv }
}


/** Synthetisiert einen kurzen "Zisch" via Web Audio API — weisses Rauschen mit
 *  Bandpass-Filter, der von ~4kHz auf ~800Hz droppt (simuliert Doppler-Effekt
 *  beim Vorbeiflug). Dauer ~350ms, Volume default 0.08. Kein Sample noetig. */
/**
 * Legt (falls noetig) einen AudioContext an und gibt ihn zurueck — MUSS im
 * Call-Stack eines User-Gestures (z.B. Click) passieren, sonst blockiert
 * Safari. Rueckgabe: Der Context, oder null wenn AudioContext nicht
 * verfuegbar ist (SSR, sehr alte Browser).
 */
export function ensureAudioContext(existing: AudioContext | null): AudioContext | null {
  if (existing) {
    if (existing.state === 'suspended') void existing.resume()
    return existing
  }
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    const ctx = new Ctor()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/**
 * Kurzer, deutlich hoerbarer Test-Piep (Sinus 880 Hz, 180 ms, Volume 0.5).
 * Wird beim ersten Sound-an-Klick zusaetzlich zum Zisch gespielt — als
 * UX-Feedback und Diagnose-Hilfe: wer den Beep hoert aber nicht den Zisch,
 * hat ein Synth-Volume-Problem, nicht ein AudioContext-Problem.
 */
export function playTestBeep(ctx: AudioContext, volume: number = 0.5) {
  const now = ctx.currentTime
  const duration = 0.18
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, now)
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)
  osc.connect(gain).connect(ctx.destination)
  osc.start(now)
  osc.stop(now + duration + 0.02)
}

/**
 * Synthetisiert einen RC-Modellsegler-Vorbeiflug via Web Audio API.
 *
 * Zwei Layer (beide aus weissem Rauschen):
 *   1) Wind-Turbulenz (Hauptklang): Bandpass mit Doppler-Sweep
 *        800 Hz → 3200 Hz (Annaeherung)
 *        → 600 Hz (Entfernen, Frequenz faellt stark)
 *   2) Rumpf-Rumpeln (tiefer Anteil): Bandpass um 180 Hz, ~55 % Lautstaerke
 *
 * Asymmetrische Huellkurve mit Peak bei 55 % der Dauer — der Moment des
 * direkten Vorbeiflugs ist akustisch der lauteste. Dauer 1.0 s.
 */
export function playZisch(ctx: AudioContext, volume: number) {
  const duration = 1.0
  const sr = ctx.sampleRate
  const N = Math.floor(sr * duration)

  // Weisses Rauschen — wird von beiden Layern geteilt
  const buffer = ctx.createBuffer(1, N, sr)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < N; i++) data[i] = Math.random() * 2 - 1

  const now = ctx.currentTime
  const peak = 0.55

  // ─── Layer 1: Wind-Turbulenz mit Doppler-Sweep ───────────────────────
  const src1 = ctx.createBufferSource()
  src1.buffer = buffer
  const hp = ctx.createBiquadFilter()
  hp.type = 'highpass'
  hp.frequency.value = 350
  hp.Q.value = 0.3
  const bp1 = ctx.createBiquadFilter()
  bp1.type = 'bandpass'
  bp1.Q.value = 1.1
  bp1.frequency.setValueAtTime(800, now)
  bp1.frequency.exponentialRampToValueAtTime(3200, now + duration * peak)
  bp1.frequency.exponentialRampToValueAtTime(600, now + duration)
  const gain1 = ctx.createGain()
  gain1.gain.setValueAtTime(0.0001, now)
  gain1.gain.exponentialRampToValueAtTime(volume, now + duration * peak)
  gain1.gain.exponentialRampToValueAtTime(0.0001, now + duration)
  src1.connect(hp).connect(bp1).connect(gain1).connect(ctx.destination)

  // ─── Layer 2: Rumpf-Rumpeln (tiefer Anteil) ──────────────────────────
  const src2 = ctx.createBufferSource()
  src2.buffer = buffer
  const bp2 = ctx.createBiquadFilter()
  bp2.type = 'bandpass'
  bp2.frequency.value = 180
  bp2.Q.value = 0.8
  const gain2 = ctx.createGain()
  gain2.gain.setValueAtTime(0.0001, now)
  gain2.gain.exponentialRampToValueAtTime(volume * 0.55, now + duration * peak)
  gain2.gain.exponentialRampToValueAtTime(0.0001, now + duration)
  src2.connect(bp2).connect(gain2).connect(ctx.destination)

  src1.start(now); src1.stop(now + duration + 0.05)
  src2.start(now); src2.stop(now + duration + 0.05)
}

export function GliderSwarm({
  configs = DEFAULT_SWARM,
  className = '',
  soundEnabled = false,
  audioContext = null,
}: {
  configs?: DriftConfig[]
  className?: string
  /** Wenn true, wird der Zisch-Sound beim Vorbeiflug gespielt. */
  soundEnabled?: boolean
  /** Ein externer AudioContext (vom Parent im User-Gesture-Handler erzeugt).
   *  Noetig, weil Safari das Erzeugen nur im Call-Stack einer User-Gesture
   *  zulaesst — und React useEffect-Aufrufe zaehlen NICHT als Gesture. */
  audioContext?: AudioContext | null
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const imgRefs = useRef<(HTMLDivElement | null)[]>([])
  const shadowRefs = useRef<(HTMLDivElement | null)[]>([])
  const prevTRef = useRef<number[]>([])
  // Refs fuer Audio-Werte, damit der RAF-Loop immer auf aktuelle Props zugreift
  // (ohne neu gestartet zu werden bei jedem soundEnabled-Flip).
  const soundEnabledRef = useRef(soundEnabled)
  const audioContextRef = useRef<AudioContext | null>(audioContext)
  useEffect(() => { soundEnabledRef.current = soundEnabled }, [soundEnabled])
  useEffect(() => { audioContextRef.current = audioContext }, [audioContext])

  // Kontinuierlicher Ambient-Audio-Graph pro Segler:
  //   source (looping noise) → highpass → bandpass → highshelf → panner → gain
  //
  // Pro Frame werden fuenf Parameter an die visuelle Bewegung gekoppelt:
  //   1) gain           ~ scale * opacity * (1 + tangentialSpeed-Bonus)
  //   2) bandpass.freq  ~ (floor + scale*range) * dopplerFactor
  //   3) bandpass.Q     ~ enger bei Ferne (scharfer Fernklang), breiter bei Naehe (voller)
  //   4) highshelf.gain ~ "weller"/heller bei Naehe (mehr Luftzug-Highs), daempft bei Ferne
  //   5) panner.pan     ~ x-Position
  //
  // dopplerFactor ergibt sich aus der Scale-Aenderungsrate dScale/dt:
  //   kommt naeher (dScale > 0) -> Faktor > 1 (Frequenz steigt)
  //   entfernt sich (dScale < 0) -> Faktor < 1 (Frequenz faellt, "dunkler")
  interface AmbientNode {
    source: AudioBufferSourceNode
    highpass: BiquadFilterNode
    bandpass: BiquadFilterNode
    highshelf: BiquadFilterNode
    panner: StereoPannerNode | null
    gain: GainNode
    // Optionaler Tragflaechen-Pfeif-Layer (Sinus-Oscillator) mit eigenem Panner
    // + direktem Output zum Destination (umgeht den Master-Gain, damit das
    // Pfeifen nicht durch den Ambient-Abfall an den Raendern mit-abgedaempft wird).
    whistle: OscillatorNode | null
    whistleGain: GainNode | null
    whistlePanner: StereoPannerNode | null
  }
  const ambientNodesRef = useRef<Array<AmbientNode | null>>([])
  const scalePrevRef = useRef<number[]>([])
  const lastFrameTsRef = useRef<number>(0)

  useEffect(() => {
    if (!audioContext) return
    const sr = audioContext.sampleRate
    const noiseDur = 2.0
    const buf = audioContext.createBuffer(1, Math.floor(sr * noiseDur), sr)
    const d = buf.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1

    const nodes: Array<AmbientNode | null> = configs.map(c => {
      if (!c.ambientSound) return null
      const source = audioContext.createBufferSource()
      source.buffer = buf
      source.loop = true
      const highpass = audioContext.createBiquadFilter()
      highpass.type = 'highpass'
      highpass.frequency.value = 220
      highpass.Q.value = 0.3
      const bandpass = audioContext.createBiquadFilter()
      bandpass.type = 'bandpass'
      bandpass.Q.value = 1.2
      bandpass.frequency.value = c.ambientFreqFloor ?? 700
      // Highshelf — wird bei Naehe geboostet (mehr Luftzug-Highs, "weller").
      const highshelf = audioContext.createBiquadFilter()
      highshelf.type = 'highshelf'
      highshelf.frequency.value = 2600
      highshelf.gain.value = -6  // Ferne: Highs gedaempft
      let panner: StereoPannerNode | null = null
      try {
        panner = audioContext.createStereoPanner()
        panner.pan.value = 0
      } catch { panner = null }
      const gain = audioContext.createGain()
      gain.gain.value = 0.0001

      if (panner) {
        source.connect(highpass).connect(bandpass).connect(highshelf).connect(panner).connect(gain).connect(audioContext.destination)
      } else {
        source.connect(highpass).connect(bandpass).connect(highshelf).connect(gain).connect(audioContext.destination)
      }
      try { source.start() } catch { /* already started, ignore */ }

      // Optionaler Pfeif-Layer (Tragflaechen-Sinus fuer schnelle Segler)
      // Eigener Panner + Direkt-Output — NICHT ueber den Master-Gain, damit
      // das Pfeifen nicht durch den Ambient-Abfall an den Bildraendern
      // mit-abgedaempft wird. whistleGain ist alleiniger Lautstaerke-Regler.
      let whistle: OscillatorNode | null = null
      let whistleGain: GainNode | null = null
      let whistlePanner: StereoPannerNode | null = null
      if (c.whistleSound) {
        whistle = audioContext.createOscillator()
        whistle.type = 'sine'
        whistle.frequency.value = c.whistleFreqLow ?? 2100
        whistleGain = audioContext.createGain()
        whistleGain.gain.value = 0.0001
        try {
          whistlePanner = audioContext.createStereoPanner()
          whistlePanner.pan.value = 0
        } catch { whistlePanner = null }

        if (whistlePanner) {
          whistle.connect(whistleGain).connect(whistlePanner).connect(audioContext.destination)
        } else {
          whistle.connect(whistleGain).connect(audioContext.destination)
        }
        try { whistle.start() } catch { /* already started, ignore */ }
      }

      return { source, highpass, bandpass, highshelf, panner, gain, whistle, whistleGain, whistlePanner }
    })
    ambientNodesRef.current = nodes
    scalePrevRef.current = configs.map(() => 0)

    return () => {
      nodes.forEach(n => {
        if (!n) return
        try { n.source.stop() } catch { /* silent */ }
        try { n.source.disconnect() } catch { /* silent */ }
        try { n.highpass.disconnect() } catch { /* silent */ }
        try { n.bandpass.disconnect() } catch { /* silent */ }
        try { n.highshelf.disconnect() } catch { /* silent */ }
        try { n.panner?.disconnect() } catch { /* silent */ }
        try { n.gain.disconnect() } catch { /* silent */ }
        try { n.whistle?.stop() } catch { /* silent */ }
        try { n.whistle?.disconnect() } catch { /* silent */ }
        try { n.whistleGain?.disconnect() } catch { /* silent */ }
        try { n.whistlePanner?.disconnect() } catch { /* silent */ }
      })
      ambientNodesRef.current = []
    }
  }, [audioContext, configs])

  // Kontext suspend/resume passend zu soundEnabled (soweit vorhanden).
  useEffect(() => {
    if (!audioContext) return
    if (soundEnabled) {
      if (audioContext.state === 'suspended') void audioContext.resume()
    } else {
      if (audioContext.state === 'running') void audioContext.suspend().catch(() => {})
    }
  }, [soundEnabled, audioContext])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    prevTRef.current = configs.map(() => 0)

    let raf = 0
    const startTs = performance.now()

    function frame(now: number) {
      const w = wrap!.clientWidth
      const h = wrap!.clientHeight
      if (w === 0 || h === 0) {
        raf = requestAnimationFrame(frame)
        return
      }
      const dt = (now - startTs) / 1000

      for (let i = 0; i < configs.length; i++) {
        const c = configs[i]
        const img = imgRefs.current[i]
        const shadow = shadowRefs.current[i]
        if (!img) continue

        const tRaw = reduce ? 0 : (dt / c.duration + c.phase)
        const tLinear = ((tRaw % 1) + 1) % 1
        // Nicht-lineare Zeit fuer echte Perspektiv-Geschwindigkeit: weit weg
        // langsam, nah schnell. timeEase=1 (default) = linear.
        const t = c.timeEase && c.timeEase !== 1
          ? Math.pow(tLinear, c.timeEase)
          : tLinear

        // Position + Tangente auf quadratischer Bézier
        const px = bezier2(c.fromX, c.cpX, c.toX, t)
        const py = bezier2(c.fromY, c.cpY, c.toY, t)

        // Delfinstil: Sinus-Welle auf Y-Achse (in Pixel). Fuer die Tangente
        // wird die Ableitung der Sinus-Komponente auf die y-Ableitung addiert,
        // damit der Bank-Winkel der Gesamtbewegung weich folgt.
        // Phase wird bei dolphinSkew > 0 verzerrt (phase - k*sin(phase)), sodass
        // der Flieger laenger im Tief bleibt (Gleitflug) und schneller wieder
        // aufsteigt (Thermik-Aufschwung). Ableitung entsprechend verkettet.
        const amp = c.dolphinAmp ?? 0
        const twoPiCycles = 2 * Math.PI * (c.dolphinCycles ?? 0)
        const k = c.dolphinSkew ?? 0
        const rawPhase = twoPiCycles * t
        const asymPhase = rawPhase - k * Math.sin(rawPhase)
        const dAsymPhase = twoPiCycles * (1 - k * Math.cos(rawPhase))
        const yExtra = amp === 0 ? 0 : amp * Math.sin(asymPhase)
        const yExtraDeriv = amp === 0 ? 0 : amp * Math.cos(asymPhase) * dAsymPhase

        const x = px.pos * w
        const y = py.pos * h + yExtra

        // Tangente in Pixel: dx = px.deriv * w, dy = py.deriv * h + Sinus-Anteil
        const tanX = px.deriv * w
        const tanY = py.deriv * h + yExtraDeriv
        const tanAngleDeg = Math.atan2(tanY, tanX) * (180 / Math.PI)
        const rotateDeg = clamp(tanAngleDeg + c.extraRotateDeg, -MAX_BANK, MAX_BANK)

        // Opacity-Hülle: an tLinear gekoppelt (nicht t-eased), damit der
        // Fade-in/out in realer Zeit passiert und bei timeEase > 1 nicht
        // halbtransparent kriecht oder am Ende schlagartig erscheint.
        const fadeIn = smoothstep(0, 0.06, tLinear)
        const fadeOut = 1 - smoothstep(0.94, 1, tLinear)
        const alpha = c.opacity * fadeIn * fadeOut

        // Perspektivischer Scale: wenn scaleMid gesetzt, zweistufig linear mit
        // Peak bei scalePeakT (default 0.5). Sonst linear scaleFrom→scaleTo.
        // Fallback: fester c.scale fuer die klassischen Flieger ohne Perspektive.
        const sFrom = c.scaleFrom ?? c.scale
        const sTo = c.scaleTo ?? c.scale
        let scaleNow: number
        if (c.scaleMid !== undefined) {
          const peakT = c.scalePeakT ?? 0.5
          if (t <= peakT) {
            scaleNow = lerp(sFrom, c.scaleMid, peakT === 0 ? 1 : t / peakT)
          } else {
            scaleNow = lerp(c.scaleMid, sTo, peakT === 1 ? 0 : (t - peakT) / (1 - peakT))
          }
        } else {
          scaleNow = lerp(sFrom, sTo, t)
        }

        img.style.transform =
          `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) rotate(${rotateDeg}deg) scale(${scaleNow})`
        img.style.opacity = String(alpha)

        if (shadow) {
          const shadowScale = 0.65 + scaleNow * 0.4
          shadow.style.transform =
            `translate3d(${x}px, ${h * 0.92}px, 0) translate(-50%, -50%) scaleX(${shadowScale})`
          shadow.style.opacity = String(alpha * 0.35)
        }

        // Zisch-Trigger: Sobald t über den Triggerpunkt springt (von kleiner
        // auf groessergleich), wird der Zisch einmal pro Loop-Durchgang
        // gespielt. Funktioniert auch beim Wrap-around (1 → 0), weil wir dann
        // prevT > t haben und der Check (prevT < trig && t >= trig) automatisch
        // false liefert — das stimmt genau mit dem Wunsch ueberein (ein Zisch
        // pro Durchgang, nicht beim Loop-Reset).
        if (c.zisch) {
          const trig = c.zischTriggerT ?? 0.5
          const prev = prevTRef.current[i] ?? 0
          if (prev < trig && t >= trig) {
            const ctx = audioContextRef.current
            if (soundEnabledRef.current && ctx && ctx.state === 'running') {
              try { playZisch(ctx, c.zischVolume ?? 0.08) } catch { /* silent */ }
            }
          }
          prevTRef.current[i] = t
        }

        // Kontinuierlicher Ambient-Sound — pro Frame alle Audio-Parameter
        // an die visuelle Bewegung koppeln. KERNIDEE: "Akustische Nähe" aus
        //   (a) Bildschirm-Position — Segler am Rand ist fern, in der Mitte nah
        //   (b) Visueller Scale — groesserer Segler klingt naeher
        // Damit bekommt jeder Segler eine klare Anflug→Vorbeiflug→Abflug-Kurve,
        // auch wenn die Scale konstant ist (wie bei Lead/Wingman/Delfin).
        if (c.ambientSound) {
          const node = ambientNodesRef.current[i]
          const ctx = audioContextRef.current
          if (node && ctx) {
            const baseGain = c.ambientBaseGain ?? 0.05
            const freqFloor = c.ambientFreqFloor ?? 700
            const freqRange = c.ambientFreqRange ?? 1600

            const framePeriod = lastFrameTsRef.current === 0
              ? 1 / 60
              : Math.max(0.001, (now - lastFrameTsRef.current) / 1000)

            // ─── Akustische Naehe aus Bildposition + Scale ────────────
            // centerProx: 1 in Bildmitte, faellt auf 0 an den Bildraendern
            // (und darueber hinaus, weil Segler von -0.3 bis 1.35 laufen)
            const centerProx = Math.max(0, 1 - Math.abs(px.pos - 0.5) * 2.2)
            // Glatte Kurve (smoothstep-aehnlich) damit Mitte deutlich nah wirkt
            const centerProxSmooth = centerProx * centerProx * (3 - 2 * centerProx)
            // Effektive Naehe = Gewichteter Mix aus Scale und Screen-Position.
            // Konstante-Scale-Segler (Lead/Wingman/Delfin) bekommen ihre
            // Annaeherungs-Dynamik damit rein aus der horizontalen Bewegung.
            const closeness = clamp(scaleNow * (0.4 + 0.6 * centerProxSmooth), 0.05, 1.3)

            // Delta fuer Doppler — vergleicht closeness mit Vorframe
            const prevCloseness = scalePrevRef.current[i] ?? closeness
            const dCloseness = (closeness - prevCloseness) / framePeriod
            scalePrevRef.current[i] = closeness
            // Staerkerer Doppler-Swing als vorher: ±30 % statt ±18 %.
            // Faktor 0.9 kalibriert, damit der Shift wirklich hoerbar ist.
            const dopplerFactor = clamp(1 + dCloseness * 0.9, 0.72, 1.32)

            // ─── Tangentiale Geschwindigkeit (Wind-Bonus) ─────────────
            const tanSpeedNorm = Math.sqrt(tanX * tanX + tanY * tanY) / Math.max(1, w)
            const speedBonus = clamp(tanSpeedNorm * 1.0, 0, 0.45)

            // ─── Gain: closeness (statt scale), staerker moduliert ────
            // alpha wird abgemildert (0.55 + 0.45*alpha) damit der Fade-In/Out
            // an den Bildraendern den Ambient-Sound nicht komplett unhoerbar
            // macht — die Modulation soll klar zum Ausdruck kommen.
            const alphaShaped = 0.55 + 0.45 * alpha
            const targetGain = soundEnabledRef.current && ctx.state === 'running'
              ? baseGain * closeness * alphaShaped * (1 + speedBonus) * 1.6
              : 0.0001

            // ─── Bandpass-Frequenz: breiter Swing mit Doppler-Shift ────
            // Range ist verdoppelt: bei voller Naehe kann die Frequenz 2x
            // hoeher sein als die Basis. Nahe + Anflug → sehr hell, Ferne +
            // Abflug → sehr dunkel (bis runter auf 0.72 × Basis).
            const targetFreq = (freqFloor + closeness * freqRange * 1.2) * dopplerFactor

            // ─── Bandpass-Q ─────────────────────────────────────────
            const targetQ = lerp(1.6, 0.6, clamp(closeness, 0, 1))

            // ─── High-Shelf-Gain (Luftzug-Highs bei Naehe) ────────────
            // bei closeness=0 (fern) -> -12 dB (deutlich dunkler)
            // bei closeness=1 (nah)  -> +6 dB (weller, Wind-Pfiff)
            const targetShelfGain = lerp(-12, 6, clamp(closeness, 0, 1))

            // ─── Stereo-Pan ─────────────────────────────────────────
            const targetPan = clamp((px.pos - 0.5) * 1.6, -1, 1)

            const tNow = ctx.currentTime
            // Smoothing reduziert (0.09 → 0.04) — damit die Modulation
            // wirklich hoerbar der Silhouette folgt und nicht verschmiert.
            const smooth = 0.04
            try {
              node.gain.gain.setTargetAtTime(Math.max(0.0001, targetGain), tNow, smooth)
              node.bandpass.frequency.setTargetAtTime(targetFreq, tNow, smooth)
              node.bandpass.Q.setTargetAtTime(targetQ, tNow, smooth * 2)
              node.highshelf.gain.setTargetAtTime(targetShelfGain, tNow, smooth * 2)
              if (node.panner) node.panner.pan.setTargetAtTime(targetPan, tNow, smooth)
            } catch { /* silent — kann bei ctx-close auftreten */ }

            // ─── Tragflaechen-Pfeifen (nur bei Seglern mit whistleSound) ─
            // Sinus-Oscillator mit eigenem Panner + Direkt-Output, NICHT durch
            // den Master-Gain. whistleGain ist alleiniger Lautstaerke-Regler.
            // Frequenz folgt closeness (low=fern, high=nah) + Doppler.
            // Gain ist quadratisch an closeness gekoppelt — scharfer Peak in
            // der Bildmitte. Der Pan wird analog zum Rauschen moduliert.
            if (c.whistleSound && node.whistle && node.whistleGain && soundEnabledRef.current && ctx.state === 'running') {
              const wLow = c.whistleFreqLow ?? 2100
              const wHigh = c.whistleFreqHigh ?? 3500
              const wBase = c.whistleBaseGain ?? 0.05
              const wClose = clamp(closeness, 0, 1)
              // Frequenz: Basis-Mapping + Doppler oben drauf
              const whistleFreq = (wLow + (wHigh - wLow) * wClose) * dopplerFactor
              // Gain: closeness^1.5 — schaerferer Peak als quadratisch, aber
              // nicht so extrem wie quartisch. Alpha bleibt aussen vor, damit
              // das Pfeifen auch zwischen den Opacity-Fades voll durchkommt.
              const whistleGainVal = wBase * Math.pow(wClose, 1.5)
              try {
                node.whistle.frequency.setTargetAtTime(whistleFreq, tNow, smooth)
                node.whistleGain.gain.setTargetAtTime(Math.max(0.0001, whistleGainVal), tNow, smooth)
                if (node.whistlePanner) node.whistlePanner.pan.setTargetAtTime(targetPan, tNow, smooth)
              } catch { /* silent */ }
            } else if (c.whistleSound && node.whistleGain) {
              // Sound disabled oder ctx suspended → Pfeifen hart auf 0
              try { node.whistleGain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.03) } catch { /* silent */ }
            }
          }
        }
      }

      // Frame-Timestamp merken fuer dScale/dt-Berechnung im naechsten Frame
      lastFrameTsRef.current = now

      if (!reduce) raf = requestAnimationFrame(frame)
    }

    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [configs])

  return (
    <div
      ref={wrapRef}
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
    >
      {configs.map((c, i) => (
        <div key={i}>
          <div
            ref={(el) => { shadowRefs.current[i] = el }}
            className="absolute left-0 top-0"
            style={{
              width: 140,
              height: 10,
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(20,32,42,0.22), transparent 70%)',
              filter: 'blur(6px)',
              willChange: 'transform, opacity',
            }}
          />
          <div
            ref={(el) => { imgRefs.current[i] = el }}
            className="award-glider absolute left-0 top-0"
            style={{
              width: c.width,
              aspectRatio: GLIDER_ASPECT,
              opacity: 0,
              willChange: 'transform, opacity',
            }}
          />
        </div>
      ))}
    </div>
  )
}
